import React from 'react';
import { AlertCircle, Check, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import type { SaveState } from '../rlhfShared/useJudgementAutosave';
import { MAX_REMARKS_LENGTH, RATING_LABELS, RATING_SCALE, SCALE_HINTS } from './constants';

interface PackagingRatingBarProps {
  rating: number | null;
  remarks: string;
  onRatingChange: (value: number) => void;
  onRemarksChange: (value: string) => void;
  onPrev: () => void;
  onNext: () => void;
  canGoPrev: boolean;
  isLast: boolean;
  busy: boolean;
  saveState: SaveState;
  error: string | null;
}

function SaveIndicator({ saveState, error }: { saveState: SaveState; error: string | null }) {
  if (saveState === 'saving') {
    return (
      <span className="flex items-center gap-1 text-xs text-gray-500">
        <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> Saving…
      </span>
    );
  }
  if (saveState === 'saved') {
    return (
      <span className="flex items-center gap-1 text-xs font-semibold text-blue-700">
        <Check className="w-3 h-3" aria-hidden="true" /> Saved
      </span>
    );
  }
  if (saveState === 'error') {
    return (
      <span className="flex items-center gap-1 text-xs font-semibold text-red-600" role="alert">
        <AlertCircle className="w-3 h-3" aria-hidden="true" /> {error ?? 'Not saved'}
      </span>
    );
  }
  return null;
}

/**
 * The sticky scoring bar, deliberately the same interaction as /rate: pick a score, type why, and
 * move on with Next — the save happens on its own. Raters work both surfaces, so a different model
 * for the same job (an explicit Save button, which this page had first) is just a trap.
 *
 * Remarks are optional here, unlike /rate. This page is open to every logged-in account rather than
 * a trained rater pool, and forcing prose from a casual rater buys filler, not signal — so Next is
 * gated on the score alone.
 */
const PackagingRatingBar: React.FC<PackagingRatingBarProps> = ({
  rating,
  remarks,
  onRatingChange,
  onRemarksChange,
  onPrev,
  onNext,
  canGoPrev,
  isLast,
  busy,
  saveState,
  error,
}) => (
  <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/97 backdrop-blur border-t border-gray-200 shadow-[0_-6px_22px_rgba(15,30,28,0.07)]">
    <div className="max-w-[1140px] mx-auto px-4 md:px-8 py-3 flex flex-col md:flex-row md:items-start gap-4 md:gap-5">
      <fieldset className="shrink-0">
        <legend className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
          Your rating
        </legend>
        <div
          className="flex gap-1.5 flex-wrap"
          role="radiogroup"
          aria-label="Quality rating from 1 to 5"
        >
          {RATING_SCALE.map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={rating === value}
              aria-label={`${value} — ${SCALE_HINTS[value]}`}
              title={SCALE_HINTS[value]}
              onClick={() => onRatingChange(value)}
              disabled={busy}
              className={`min-w-[68px] min-h-[44px] px-2 py-1 rounded-xl border-[1.5px]
                          flex flex-col items-center justify-center gap-0.5
                          transition-all hover:-translate-y-px focus:outline-none
                          disabled:opacity-60 focus:ring-2 focus:ring-blue-500
                          focus:ring-offset-1 ${
                            rating === value
                              ? 'bg-gray-900 border-gray-900 text-white'
                              : 'bg-white border-gray-200 text-gray-500 hover:border-blue-600'
                          }`}
            >
              <span className="text-[15px] font-semibold leading-none">{value}</span>
              <span className="text-[9px] leading-none whitespace-nowrap">
                {RATING_LABELS[value]}
              </span>
            </button>
          ))}
        </div>
      </fieldset>

      <div className="flex-1 min-w-0">
        <label
          htmlFor="packaging-remarks"
          className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5"
        >
          Why this score?{' '}
          <span className="font-normal normal-case tracking-normal">(optional)</span>
        </label>
        <textarea
          id="packaging-remarks"
          value={remarks}
          onChange={(e) => onRemarksChange(e.target.value)}
          disabled={busy}
          maxLength={MAX_REMARKS_LENGTH}
          placeholder="What worked, what didn't, what you'd change…"
          rows={2}
          className="w-full min-h-[42px] rounded-xl border-[1.5px] border-gray-200 px-3 py-2 text-[13px] resize-y focus:outline-none focus:border-blue-600 disabled:opacity-60"
        />
        <p className="text-[11px] text-gray-500 mt-1">
          {rating === null
            ? 'Pick a score to start saving — your notes are kept as you move between reports.'
            : 'Autosaves as you type · saved automatically when you press Next'}
        </p>
      </div>

      <div className="flex items-center gap-2 md:ml-auto shrink-0 flex-wrap">
        <SaveIndicator saveState={saveState} error={error} />
        <button
          type="button"
          onClick={onPrev}
          disabled={!canGoPrev || busy}
          className="px-4 py-2 min-h-[44px] rounded-xl border-[1.5px] border-gray-200 bg-white text-[13px] font-semibold disabled:opacity-40 flex items-center gap-1"
        >
          <ChevronLeft className="w-4 h-4" aria-hidden="true" /> Prev
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={busy || rating === null}
          title={rating === null ? 'Pick a score first' : undefined}
          className="px-4 py-2 min-h-[44px] rounded-xl bg-gray-900 text-white text-[13px] font-semibold hover:bg-gray-800 disabled:opacity-40 flex items-center gap-1"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null}
          {isLast ? 'Finish' : 'Next'}
          {!isLast && <ChevronRight className="w-4 h-4" aria-hidden="true" />}
        </button>
      </div>
    </div>
  </div>
);

export default PackagingRatingBar;
